
import React from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { SubscriptionPlan } from '@/hooks/subscriptions/types';

interface SubscriptionPlanCardProps {
  plan: SubscriptionPlan;
  isCurrentPlan?: boolean;
  onSelect: (plan: SubscriptionPlan) => void;
  isLoading?: boolean;
}

export function SubscriptionPlanCard({ plan, isCurrentPlan = false, onSelect, isLoading = false }: SubscriptionPlanCardProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount / 100);
  };

  // Count the number of enabled features
  const enabledFeatures = Object.values(plan.features).filter(Boolean).length;
  const totalFeatures = Object.keys(plan.features).length;

  // Generate feature list to display
  const featuresList = Object.entries(plan.features).map(([key, enabled]) => {
    const featureName = key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    return { name: featureName, enabled };
  });

  return (
    <Card className={`flex flex-col h-full transition-all ${
      isCurrentPlan ? 'border-primary shadow-md shadow-primary/20' : 'hover:border-primary/50'
    }`}>
      <CardHeader>
        {isCurrentPlan && (
          <Badge className="w-fit mb-2" variant="outline">
            Your Plan
          </Badge>
        )}
        <CardTitle className="text-2xl font-bold">{plan.name}</CardTitle>
        <CardDescription className="h-12 overflow-hidden text-ellipsis">
          {plan.description}
        </CardDescription>
        <div className="mt-4">
          <span className="text-3xl font-bold">{formatCurrency(plan.monthly_price)}</span>
          <span className="text-muted-foreground">/month</span>
        </div>
      </CardHeader>
      <CardContent className="flex-grow">
        <p className="text-sm text-muted-foreground mb-4">
          Includes {enabledFeatures} of {totalFeatures} features
        </p>
        <ul className="space-y-2">
          {featuresList.map((feature, index) => (
            <li key={index} className="flex items-center">
              <span className={`mr-2 ${feature.enabled ? 'text-primary' : 'text-muted-foreground'}`}>
                {feature.enabled ? <Check size={16} /> : '—'}
              </span>
              <span className={feature.enabled ? '' : 'text-muted-foreground line-through'}>
                {feature.name}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
      <CardFooter className="pt-4 mt-auto">
        <Button 
          variant={isCurrentPlan ? "secondary" : "default"} 
          className="w-full" 
          onClick={() => onSelect(plan)}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processing...
            </>
          ) : isCurrentPlan ? (
            'Current Plan'
          ) : (
            'Select Plan'
          )}
        </Button>
      </CardFooter>
    </Card>
  );
}
